import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import "./glass-card.css";

const glassCardVariants = cva(
  "glass-card relative overflow-hidden backdrop-blur-md border transition-all duration-300",
  {
    variants: {
      variant: {
        default: [
          "bg-background/40 border-border/50",
          "dark:bg-background/30 dark:border-border/30",
          "shadow-lg dark:shadow-2xl",
        ],
        subtle: [
          "bg-background/20 border-border/30",
          "dark:bg-background/15 dark:border-border/20",
          "shadow-md dark:shadow-xl",
        ],
        strong: [
          "bg-background/60 border-border/70",
          "dark:bg-background/50 dark:border-border/50",
          "shadow-xl dark:shadow-2xl",
        ],
        frosted: [
          "bg-white/30 border-white/40",
          "dark:bg-black/30 dark:border-white/10",
          "shadow-lg dark:shadow-2xl backdrop-blur-lg",
        ],
        tinted: [
          "bg-primary/10 border-primary/30",
          "dark:bg-primary/5 dark:border-primary/20",
          "shadow-lg dark:shadow-xl",
        ],
        cardLike: [
          "bg-gradient-to-card from-card/50 to-card/30",
          // "dark:from-background/40 dark:to-background/20",
          "border-border/60 dark:border-border/40",
          "shadow-2xl dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.4)]",
        ],
        premium: [
          "bg-gradient-to-br from-background/50 to-background/30",
          "dark:from-background/40 dark:to-background/20",
          "border-border/60 dark:border-border/40",
          "shadow-2xl dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.4)]",
        ],
        destructive: [
          "bg-destructive/10 border-destructive/40",
          "dark:bg-destructive/5 dark:border-destructive/30",
          "shadow-lg shadow-destructive/20 dark:shadow-xl dark:shadow-destructive/10",
        ],
        warning: [
          "bg-yellow-500/10 border-yellow-500/40",
          "dark:bg-yellow-500/5 dark:border-yellow-500/30",
          "shadow-lg shadow-yellow-500/20 dark:shadow-xl dark:shadow-yellow-500/10",
        ],
        error: [
          "bg-red-500/10 border-red-500/40",
          "dark:bg-red-500/5 dark:border-red-500/30",
          "shadow-lg shadow-red-500/20 dark:shadow-xl dark:shadow-red-500/10",
        ],
      },
      blur: {
        none: "backdrop-blur-none",
        sm: "backdrop-blur-sm",
        md: "backdrop-blur-md",
        lg: "backdrop-blur-lg",
        xl: "backdrop-blur-xl",
        "2xl": "backdrop-blur-2xl",
      },
      glow: {
        none: "",
        subtle: "glass-glow-subtle",
        medium: "glass-glow-medium",
        strong: "glass-glow-strong",
      },
      shine: {
        none: "",
        subtle: "glass-shine-subtle",
        medium: "glass-shine-medium",
        strong: "glass-shine-strong",
      },
    },
    defaultVariants: {
      variant: "default",
      blur: "md",
      glow: "subtle",
      shine: "subtle",
    },
  },
);

export interface GlassCardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof glassCardVariants> {
  children?: ReactNode;
  className?: string;
  hover?: boolean;
  animated?: boolean;
  imageUrl?: string;
  imageOpacity?: number;
}

export default function GlassCard({
  children,
  className,
  variant,
  blur,
  glow,
  shine,
  hover = true,
  animated = false,
  imageUrl,
  imageOpacity = 0.3,
  ...props
}: GlassCardProps) {
  return (
    <Card
      className={cn(
        glassCardVariants({ variant, blur, glow, shine }),
        hover &&
          "hover:shadow-2xl hover:scale-[1.02] hover:border-primary/70 dark:hover:border-primary/50",
        animated && "glass-animated relative",
        className,
      )}
      style={{
        ...(imageUrl && {
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }),
        ...props.style,
      }}
      {...props}
    >
      {/* Background image overlay with opacity control */}
      {imageUrl && (
        <div
          className="absolute inset-0 -z-1"
          style={{
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: imageOpacity,
          }}
          aria-hidden="true"
        />
      )}

      {/* Content container - ensures content stays above the background */}
      <div className="relative z-10">{children}</div>
    </Card>
  );
}

export { glassCardVariants };
